import { Mic, MicOff } from 'lucide-react';
import PropTypes from 'prop-types';
import React from 'react';

/**
 * Speech recognition status indicator
 */
const SpeechRecognitionStatus = ({ status }) => {
  const getStatusConfig = () => {
    switch (status) {
      case 'listening':
        return {
          bgClass: 'bg-green-100 text-green-800',
          dotClass: 'bg-green-500 animate-pulse',
          text: 'Listening...',
        };
      case 'no-speech':
        return {
          bgClass: 'bg-yellow-100 text-yellow-800',
          dotClass: 'bg-yellow-500',
          text: 'Waiting for speech...',
        };
      case 'error':
        return {
          bgClass: 'bg-red-100 text-red-800',
          dotClass: 'bg-red-500',
          text: 'Speech recognition error',
        };
      default:
        return {
          bgClass: 'bg-gray-100 text-gray-600',
          dotClass: 'bg-gray-400',
          text: 'Initializing...',
        };
    }
  };

  const config = getStatusConfig();

  return (
    <div className="mb-3 flex items-center justify-center">
      <div className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-2 ${config.bgClass}`}>
        <div className={`w-2 h-2 rounded-full ${config.dotClass}`} />
        <span>{config.text}</span>
      </div>
    </div>
  );
};

SpeechRecognitionStatus.propTypes = {
  status: PropTypes.oneOf(['idle', 'listening', 'no-speech', 'error']).isRequired,
};

/**
 * Audio visualizer canvas wrapper
 */
const AudioVisualizer = ({ canvasRef }) => {
  return (
    <div className="flex justify-center mb-4">
      <canvas
        ref={canvasRef}
        width={300}
        height={60}
        className="w-[300px] h-[60px] rounded-lg border border-gray-200"
      />
    </div>
  );
};

AudioVisualizer.propTypes = {
  canvasRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
};

/**
 * Text input form for typing responses
 */
const TextInputForm = ({ textInput, onTextInputChange, onSubmit, disabled }) => {
  return (
    <form onSubmit={onSubmit} className="mb-4">
      <div className="flex gap-2">
        <input
          type="text"
          value={textInput}
          onChange={onTextInputChange}
          placeholder="Type your response instead of speaking..."
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          disabled={disabled}
        />
        <button
          type="submit"
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium transition-colors"
          disabled={disabled || !textInput.trim()}
        >
          Send
        </button>
      </div>
    </form>
  );
};

TextInputForm.propTypes = {
  textInput: PropTypes.string.isRequired,
  onTextInputChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
};

/**
 * Main recording controls component
 */
const RecordingControls = ({
  isRecording,
  isSpeaking,
  speechEnabled,
  speechRecognitionStatus,
  canvasRef,
  textInput,
  onTextInputChange,
  onTextInputSubmit,
  onStartRecording,
  onStopRecording,
  isSpeechRecognitionSupported = true,
}) => {
  return (
    <div className="flex-none border-t border-gray-200 p-4 bg-white">
      {/* Speech Recognition Status Indicator */}
      {isRecording && <SpeechRecognitionStatus status={speechRecognitionStatus} />}

      {/* Audio visualizer canvas */}
      <AudioVisualizer canvasRef={canvasRef} />

      {/* Text input form */}
      <TextInputForm
        textInput={textInput}
        onTextInputChange={onTextInputChange}
        onSubmit={onTextInputSubmit}
        disabled={isRecording || isSpeaking}
      />

      {/* Voice unavailable (e.g. Firefox): typing above still works. */}
      {!isSpeechRecognitionSupported && (
        <p className="text-xs text-gray-500 text-center">
          Voice input isn&apos;t supported in this browser. Type your answers above, or
          switch to Chrome, Edge, or Safari to speak them.
        </p>
      )}

      {/* Record/Stop button */}
      <button
        onClick={isRecording ? onStopRecording : onStartRecording}
        disabled={isSpeaking || !isSpeechRecognitionSupported}
        className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
          isRecording
            ? 'bg-red-500 hover:bg-red-600 shadow-red-100'
            : 'bg-blue-500 hover:bg-blue-600 shadow-blue-100'
        } text-white shadow-lg disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        {isRecording ? (
          <>
            <MicOff className="h-5 w-5" />
            Stop Recording
          </>
        ) : (
          <>
            <Mic className="h-5 w-5" />
            Start Recording
          </>
        )}
      </button>

      {/* Speaking indicator */}
      {isSpeaking && speechEnabled && (
        <div className="mt-3 text-center text-sm text-gray-500 animate-pulse">
          InternHub Cat is speaking...
        </div>
      )}
    </div>
  );
};

RecordingControls.propTypes = {
  isRecording: PropTypes.bool.isRequired,
  isSpeaking: PropTypes.bool.isRequired,
  speechEnabled: PropTypes.bool.isRequired,
  speechRecognitionStatus: PropTypes.oneOf(['idle', 'listening', 'no-speech', 'error']).isRequired,
  canvasRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
  textInput: PropTypes.string.isRequired,
  onTextInputChange: PropTypes.func.isRequired,
  onTextInputSubmit: PropTypes.func.isRequired,
  onStartRecording: PropTypes.func.isRequired,
  onStopRecording: PropTypes.func.isRequired,
  isSpeechRecognitionSupported: PropTypes.bool,
};

export default RecordingControls;
